// ===========================================
// HOOK: useCatalogoImpuestos
// ===========================================
// Hook para cargar y gestionar el catalogo de impuestos

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { useSupabase } from '@/lib/supabase-context'
import { usePermissions } from '@/hooks/usePermissions'
import { assertPermiso } from '@/lib/permisos-guard'
import {
  obtenerCatalogoImpuestos,
  crearImpuesto,
  actualizarImpuesto,
  desactivarImpuesto,
} from '@/services/catalogoImpuestosService'
import type { CategoriaImpuestoCatalogo, ColorImpuesto } from '@/types/impuestos'
import { impuestosPageKeys } from '@/hooks/useImpuestosPage'

export function useCatalogoImpuestos() {
  const { organizacionId } = useOrganizacion()
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const queryClient = useQueryClient()
  const { puede } = usePermissions()

  // La página de Impuestos arma su propio catálogo con métricas (`impuestosPageKeys.catalogo`).
  const invalidarCatalogo = () => {
    queryClient.invalidateQueries({ queryKey: ['catalogo-impuestos', organizacionId] })
    queryClient.invalidateQueries({ queryKey: impuestosPageKeys.all })
  }

  const query = useQuery({
    queryKey: ['catalogo-impuestos', organizacionId],
    queryFn: () => {
      if (!organizacionId) throw new Error('No organization')
      return obtenerCatalogoImpuestos(supabase!, organizacionId)
    },
    enabled: !!supabase && !supabaseLoading && !!organizacionId,
    staleTime: 1000 * 60 * 5, // 5 minutos
  })

  const crearMutation = useMutation({
    mutationFn: (input: {
      nombre: string
      nombre_corto?: string
      categoria?: CategoriaImpuestoCatalogo
      color?: ColorImpuesto
    }) => {
      assertPermiso(puede, 'editarCatalogoImpuestos')
      if (!organizacionId) throw new Error('No organization')
      return crearImpuesto(supabase!, organizacionId, input)
    },
    onSuccess: invalidarCatalogo,
  })

  const actualizarMutation = useMutation({
    mutationFn: (input: {
      id: string
      nombre?: string
      nombre_corto?: string
      categoria?: CategoriaImpuestoCatalogo
      color?: ColorImpuesto
      orden?: number
      activo?: boolean
    }) => {
      assertPermiso(puede, 'editarCatalogoImpuestos')
      if (!organizacionId) throw new Error('No organization')
      const { id, ...data } = input
      return actualizarImpuesto(supabase!, organizacionId, id, data)
    },
    onSuccess: invalidarCatalogo,
  })

  const desactivarMutation = useMutation({
    mutationFn: (id: string) => {
      assertPermiso(puede, 'editarCatalogoImpuestos')
      if (!organizacionId) throw new Error('No organization')
      return desactivarImpuesto(supabase!, organizacionId, id)
    },
    onSuccess: invalidarCatalogo,
  })

  return {
    catalogo: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    crearImpuesto: crearMutation.mutateAsync,
    isCreating: crearMutation.isPending,
    actualizarImpuesto: actualizarMutation.mutateAsync,
    isUpdating: actualizarMutation.isPending,
    desactivarImpuesto: desactivarMutation.mutateAsync,
    isDeleting: desactivarMutation.isPending,
  }
}
